// src/services/auth.service.ts
"use client";

import { apiClient } from "@/lib/axios";
import { VerifyOutput } from "@/types/auth.types";
import { AptosSignInInput } from "@aptos-labs/wallet-adapter-react";

interface JWTPayload {
  address: string;
  publicKey: string;
  chainId: string;
  application: string;
  iat: number;
  exp: number;
  aud: string;
  iss: string;
}

interface SerializedAptosSignInOutput {
  input: AptosSignInInput;
  signature: string;
  publicKey: string;
  type: string;
  version: string;
}

interface SiwaAuthResponse {
  output: VerifyOutput;
  accessToken: string;
}

export class AuthService {
  static async getSignInInput(): Promise<{ input: AptosSignInInput }> {
    const response = await apiClient.get<AptosSignInInput>("/auth/siwa/input");
    return { input: response.data };
  }

  static async siwaAuthenticate(request: SerializedAptosSignInOutput): Promise<SiwaAuthResponse> {
    const response = await apiClient.post<VerifyOutput>("/auth/siwa/verify", {
      output: request,
    });

    // Token is in response.data.token
    const token = response.data.token;

    if (!token) {
      throw new Error("Missing authentication token in response");
    }

    return {
      output: {
        success: true,
        token: token,
        user: {
          address: response.data.user.address,
          chainId: response.data.user.chainId,
        },
      },
      accessToken: token,
    };
  }

  static async siwaValidate(request: SerializedAptosSignInOutput): Promise<SiwaAuthResponse> {
    const response = await apiClient.post<VerifyOutput>("/auth/siwa/validate", {
      output: request,
    });

    if (!response?.data?.token) {
      throw new Error("Missing authentication tokens in response");
    }

    return {
      output: response.data,
      accessToken: response.data.token,
    };
  }

  static async signOut(): Promise<void> {
    try {
      await apiClient.post("/auth/logout", {}, {
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
    } catch (error) {
      // Continue with local logout even if server request fails
      console.error("Sign out error:", error);
    }
  }

  static async validateToken(token?: string): Promise<{ token: string; valid: boolean; decoded?: JWTPayload; error?: string }> {
    try {
      // If no token provided, the backend will check the cookie automatically
      const response = await apiClient.post("/auth/validate", {
        token: token || "",
      });

      return response.data;
    } catch (error) {
      console.error("Token validation error:", error);
      return {
        valid: false,
        error: "Failed to validate token",
        token: token || "",
      };
    }
  }

  static async checkAuthStatus(): Promise<{ isAuthenticated: boolean; verifyOutput?: VerifyOutput }> {
    try {
      // This will automatically include cookies due to withCredentials: true
      const validationResult = await AuthService.validateToken();

      if (validationResult.valid && validationResult.decoded) {
        // Token is valid, extract user info if available
        const user = validationResult.decoded.address;

        return {
          isAuthenticated: true,
          verifyOutput: {
            success: true,
            token: validationResult.token,
            user: {
              address: user,
              chainId: validationResult.decoded.chainId,
            },
          },
        };
      }

      return { isAuthenticated: false };
    } catch (error) {
      console.error("Auth status check error:", error);
      return { isAuthenticated: false };
    }
  }
}
